import React, { useMemo } from 'react';
import { getPlaceholderImageUrl } from '../../../utils/games/unlimitedGameImages';
import { getZoomTransform } from '../../../utils/zoomPosition';

const GameImage = ({
  imageLoading,
  imageUrl,
  zoomLevel,
  zoomOrigin,
  zoomBounds,
  isChecking,
  disableTransition,
  disableImageReveal = false,
  waitingForFallback,
  solution,
  transitionOverride,
  onLoad,
  onError,
  onImageClick,
  children,
}) => {
  const transform = useMemo(
    () => getZoomTransform(zoomOrigin, zoomLevel, zoomBounds),
    [zoomOrigin, zoomLevel, zoomBounds]
  );
  const isImageInteractive = typeof onImageClick === 'function';

  return (
    <div className="image-container">
      {imageLoading && (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <span className="loading-text">Loading image...</span>
        </div>
      )}
      {imageUrl && (
        <>
          <div style={{ display: 'none' }}>Debug URL: {imageUrl}</div>
          <img
            src={imageUrl}
            alt="Guess the word"
            loading="lazy"
            onLoad={onLoad}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = getPlaceholderImageUrl(solution);
              if (onError) onError(e);
            }}
            onClick={
              isImageInteractive
                ? (e) => {
                    const clickedSrc = e.currentTarget?.currentSrc || e.currentTarget?.src || imageUrl;
                    onImageClick(clickedSrc);
                  }
                : undefined
            }
            onContextMenu={
              disableImageReveal
                ? (event) => {
                    event.preventDefault();
                  }
                : undefined
            }
            onDragStart={
              disableImageReveal
                ? (event) => {
                    event.preventDefault();
                  }
                : undefined
            }
            onKeyDown={
              isImageInteractive
                ? (e) => {
                    if (e.key !== 'Enter' && e.key !== ' ') return;
                    e.preventDefault();
                    const clickedSrc = e.currentTarget?.currentSrc || e.currentTarget?.src || imageUrl;
                    onImageClick(clickedSrc);
                  }
                : undefined
            }
            role={isImageInteractive ? 'button' : undefined}
            tabIndex={isImageInteractive ? 0 : undefined}
            aria-label={isImageInteractive ? 'Expand generated image' : undefined}
            title={isImageInteractive ? 'Tap to enlarge' : undefined}
            className={isImageInteractive ? 'zi-image-clickable' : undefined}
            draggable={disableImageReveal ? false : undefined}
            style={{
              width: `${zoomLevel}%`,
              height: `${zoomLevel}%`,
              objectFit: 'cover',
              transform,
              transition: disableTransition
                ? 'none'
                : transitionOverride ||
                  (isChecking ? 'all 0.8s ease-in-out' : 'all 0.08s ease-in-out'),
              opacity: imageLoading || waitingForFallback ? 0 : 1,
              cursor: isImageInteractive ? 'zoom-in' : 'default',
              userSelect: disableImageReveal ? 'none' : undefined,
              WebkitUserSelect: disableImageReveal ? 'none' : undefined,
              WebkitTouchCallout: disableImageReveal ? 'none' : undefined,
            }}
          />
        </>
      )}
      {children}
    </div>
  );
};

export default GameImage;
